'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { trackEvent } from '@/lib/track-event'

/**
 * content_scroll_75 — fires once when the reader reaches ≥75% of the actual
 * article content (not the site-wide page). Renders nothing.
 *
 * Implementation: IntersectionObserver (the pattern the repo already prefers
 * over scroll listeners). A 1px
 * aria-hidden sentinel is positioned at top:75% inside the article content
 * element ([data-article-content]); when it scrolls into view the reader has
 * passed the 75% mark. No scroll handler, no high-frequency processing, and it
 * reflows automatically with the content on resize.
 *
 * Duplicate protection: a module-level Set keyed by article id means the event
 * fires at most once per article across React Strict Mode double-mounts,
 * client rerenders, and SPA navigations that return to the same article.
 * Observer + sentinel are torn down on unmount.
 *
 * Consent: trackEvent() is already consent-gated (no-op without consent), so
 * no extra gate is needed here.
 */

const firedArticles = new Set<string>()

interface ArticleScrollDepthProps {
  /** Canonical article slug/id (post.slug || flattenedPath). */
  articleId: string
}

export default function ArticleScrollDepth({
  articleId,
}: ArticleScrollDepthProps) {
  const pathname = usePathname()

  useEffect(() => {
    if (typeof window === 'undefined' || !articleId) return
    if (firedArticles.has(articleId)) return

    const content = document.querySelector<HTMLElement>(
      '[data-article-content]'
    )
    if (!content) return

    // The sentinel is absolutely positioned relative to the content element.
    // Only promote a static content box to relative; restore on cleanup.
    const hadStaticPosition = getComputedStyle(content).position === 'static'
    if (hadStaticPosition) content.style.position = 'relative'

    const sentinel = document.createElement('div')
    sentinel.setAttribute('aria-hidden', 'true')
    sentinel.style.cssText =
      'position:absolute;top:75%;left:0;width:1px;height:1px;pointer-events:none;'
    content.appendChild(sentinel)

    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return
      if (firedArticles.has(articleId)) return
      firedArticles.add(articleId)
      trackEvent('content_scroll_75', {
        page: pathname,
        article_id: articleId,
      })
      observer.disconnect()
    })
    observer.observe(sentinel)

    return () => {
      observer.disconnect()
      sentinel.remove()
      if (hadStaticPosition) content.style.position = ''
    }
  }, [articleId, pathname])

  return null
}

/** Test-only: clear the fired-articles guard between vitest cases. */
export function _resetArticleScrollDepthForTests(): void {
  firedArticles.clear()
}
